import { useState, useEffect, useRef } from "react";
import { Upload, X, Image } from "lucide-react";

export default function LogoUpload({ logoUrl, onLogoChange, onLogoRemove }) {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);
  const [displayUrl, setDisplayUrl] = useState(null);
  const fileInputRef = useRef(null);

  // Load logo for display when logoUrl changes
  useEffect(() => {
    if (!logoUrl) {
      setDisplayUrl(null);
      return;
    }

    // If logoUrl is already a data URL (base64), use it directly
    if (logoUrl.startsWith('data:')) {
      setDisplayUrl(logoUrl);
    } else {
      // Otherwise, assume it's a regular URL
      setDisplayUrl(logoUrl);
    }
  }, [logoUrl]);

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/svg+xml'];
    if (!validTypes.includes(file.type)) {
      setError('Invalid file type. Please upload PNG, JPG, GIF, or SVG.');
      return;
    }

    // Validate file size (max 2MB)
    const maxSize = 2 * 1024 * 1024; // 2MB
    if (file.size > maxSize) {
      setError('File too large. Maximum size is 2MB.');
      return;
    }

    setIsUploading(true);
    setError(null);

    try {
      // Convert file to base64
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64Data = e.target.result;
        // Call parent handler with base64 data
        onLogoChange(base64Data, file.name);
        setIsUploading(false);
      };
      reader.onerror = () => {
        setError('Failed to read file');
        setIsUploading(false);
      };
      reader.readAsDataURL(file);
    } catch (err) {
      console.error("Logo upload error:", err);
      setError("Failed to upload logo");
      setIsUploading(false);
    }
  };

  const handleUpload = () => {
    fileInputRef.current?.click();
  };

  const handleRemove = () => {
    try {
      onLogoRemove();
      setError(null);
    } catch (err) {
      console.error("Logo remove error:", err);
      setError("Failed to remove logo");
    }
  };

  return (
    <div className="space-y-3">
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/jpg,image/gif,image/svg+xml"
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Current Logo Display */}
      {displayUrl && (
        <div className="relative inline-block">
          <div className="w-32 h-20 border-2 border-slate-200 rounded-lg overflow-hidden bg-slate-50 flex items-center justify-center">
            <img
              src={displayUrl}
              alt="Company Logo"
              className="max-w-full max-h-full object-contain"
              onError={() => setError("Failed to load logo image")}
            />
          </div>
          <button
            onClick={handleRemove}
            className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
            title="Remove logo"
          >
            <X size={12} />
          </button>
        </div>
      )}

      {/* Upload Button */}
      {!displayUrl && (
        <div className="w-32 h-20 border-2 border-dashed border-slate-300 rounded-lg flex flex-col items-center justify-center bg-slate-50 hover:bg-slate-100 transition-colors">
          <button
            onClick={handleUpload}
            disabled={isUploading}
            className="flex flex-col items-center justify-center w-full h-full text-slate-500 hover:text-slate-700 transition-colors disabled:opacity-50"
          >
            {isUploading ? (
              <div className="animate-spin w-5 h-5 border-2 border-slate-300 border-t-slate-600 rounded-full"></div>
            ) : (
              <>
                <Upload size={16} className="mb-1" />
                <span className="text-xs">Upload Logo</span>
              </>
            )}
          </button>
        </div>
      )}

      {/* Replace Button (when logo exists) */}
      {displayUrl && (
        <button
          onClick={handleUpload}
          disabled={isUploading}
          className="px-3 py-1.5 text-xs font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-md transition-colors disabled:opacity-50 flex items-center gap-1"
        >
          <Image size={12} />
          {isUploading ? "Uploading..." : "Replace Logo"}
        </button>
      )}

      {/* Error Message */}
      {error && (
        <p className="text-xs text-red-600 bg-red-50 px-2 py-1 rounded">
          {error}
        </p>
      )}

      {/* Help Text */}
      <p className="text-xs text-slate-500">
        Supported formats: PNG, JPG, GIF, SVG. Recommended size: 200x100px or similar ratio.
      </p>
    </div>
  );
}